import React, { useState, useCallback } from "react";
import { Box } from "@mui/material";
import { Check } from "@mui/icons-material";
import { StriimModal, StriimInputField, StriimTypography } from "@striim/striim-ui-v2";
import useDebounce from "src/hooks/useDebounce";
import { getFlowNameValidation } from "../../../apps/pages/create-app-name-app-page/name.validation";
import { newSubflowDialogStyles } from "./add-flow.styles";

interface NewSubflowDialogProps {
    isOpen: boolean;
    namespace: string;
    onSubmit: (flowName: string) => Promise<void>;
    onCancel: () => void;
}

const NewSubflowDialog: React.FC<NewSubflowDialogProps> = ({ isOpen, namespace, onSubmit, onCancel }) => {
    const [flowName, setFlowName] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [isValidating, setIsValidating] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const debouncedValidate = useDebounce(async () => {
        const validationError = await getFlowNameValidation(namespace, flowName);
        setError(validationError);
        setIsValidating(false);
    }, 300);

    const handleNameChange = useCallback(
        (value: string) => {
            setFlowName(value);
            setError(null);

            if (!value) {
                setIsValidating(false);
                return;
            }

            setIsValidating(true);
            debouncedValidate();
        },
        [debouncedValidate]
    );

    const handleSubmit = useCallback(async () => {
        if (isSubmitting || isValidating) {
            return;
        }

        const validationError = await getFlowNameValidation(namespace, flowName);

        if (validationError) {
            setError(validationError);

            return;
        }

        setIsSubmitting(true);
        try {
            await onSubmit(flowName);
        } finally {
            setIsSubmitting(false);
        }
    }, [flowName, namespace, isSubmitting, isValidating, onSubmit]);

    const handleCancel = useCallback(() => {
        setFlowName("");
        setError(null);
        setIsSubmitting(false);
        onCancel();
    }, [onCancel]);

    const isValid = flowName && !error && !isValidating;

    return (
        <StriimModal
            headerBorder={true}
            footerBorder={true}
            autoHeight={true}
            isVisible={isOpen}
            size="small"
            onCancel={handleCancel}
            onConfirm={handleSubmit}
            titleContent={<StriimTypography variant="h3">Create new flow</StriimTypography>}
            cancelContent="Cancel"
            confirmContent="Save"
            confirmButtonProps={{
                disabled: !isValid || isSubmitting,
                startIcon: <Check />,
                id: "submit"
            }}
            cancelButtonProps={{
                disabled: isSubmitting
            }}
            dialogProps={{
                // @ts-ignore
                sx: { ...newSubflowDialogStyles.wrapper }
            }}
        >
            <Box sx={newSubflowDialogStyles.content}>
                <StriimInputField
                    label="Name"
                    placeholder="Name"
                    value={flowName}
                    onChange={handleNameChange}
                    error={!!error}
                    helperText={error || ""}
                    autoFocus
                    fullWidth
                    disabled={isSubmitting}
                    id="name"
                />
            </Box>
        </StriimModal>
    );
};

export default NewSubflowDialog;
